import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ConfirmButton } from "@/pages/admin/AdminShared";
import { METRIC_UNIT, TYPE_LABEL } from "@/pages/admin/ChallengeForm";
import { Play, Pause, Square, Pencil, UserX, RotateCcw, ScrollText, Award } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");
const STATUS_STYLE = { draft: "bg-zinc-800 text-zinc-300", active: "bg-emerald-500/15 text-emerald-300", paused: "bg-amber-500/15 text-amber-300", ended: "bg-rose-950/60 text-rose-300" };
export const StatusPill = ({ s }) => <span data-testid="challenge-status" className={`text-[9px] uppercase tracking-widest px-2 py-0.5 ${STATUS_STYLE[s] || ""}`}>{s}</span>;

export default function ChallengeDetail({ id, onEdit, onChanged, me }) {
  const [c, setC] = useState(null);
  const [audit, setAudit] = useState(null);
  const [showAudit, setShowAudit] = useState(false);

  const load = useCallback(() => api.get(`/admin/challenges/${id}`).then(({ data }) => setC(data)).catch(() => toast.error("Couldn't load challenge.")), [id]);
  useEffect(() => { setShowAudit(false); setAudit(null); load(); }, [load]);

  const act = async (action) => {
    const reason = action === "end" ? (window.prompt("End this challenge now? Optional reason:", "") ?? null) : undefined;
    if (action === "end" && reason === null) return;
    try {
      const { data } = await api.post(`/admin/challenges/${id}/status`, { action, reason: reason || undefined });
      setC((prev) => ({ ...prev, ...data }));
      toast.success(`Challenge ${data.status}.`);
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Status change failed."); }
  };
  const disqualify = async (row) => {
    const reason = window.prompt(`Disqualify @${row.handle}? Reason (required):`, "");
    if (!reason || reason.trim().length < 2) return;
    try {
      await api.post(`/admin/challenges/${id}/disqualify`, { user_id: row.user_id, reason: reason.trim() });
      toast.success(`@${row.handle} disqualified.`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Disqualify failed."); }
  };
  const awardBadge = async (row) => {
    if (!window.confirm(`Award @${row.handle} the Top Badge for 30 days? After that it stays only while they meet the monthly 25 h + 50,000 🎋 goals.`)) return;
    try {
      const { data } = await api.post(`/admin/challenges/${id}/award-badge`, { user_id: row.user_id, days: 30 });
      toast.success(`Top Badge awarded to @${data.handle} until ${fmt(data.top_badge_exempt_until)}.`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Award failed."); }
  };
  const reinstate = async (row) => {
    try { await api.delete(`/admin/challenges/${id}/disqualify/${row.user_id}`); toast.success(`@${row.handle} reinstated.`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reinstate failed."); }
  };
  const toggleAudit = async () => {
    if (!showAudit && !audit) {
      try { const { data } = await api.get(`/admin/challenges/${id}/audit`); setAudit(data.rows); } catch { toast.error("Couldn't load audit."); }
    }
    setShowAudit((v) => !v);
  };

  if (!c) return <div className="card-surface p-6 text-xs text-zinc-500">Loading…</div>;
  const unit = METRIC_UNIT[c.type];
  const board = c.leaderboard || [];
  const paused = c.status === "paused";

  return (
    <div data-testid="challenge-detail" className="space-y-4">
      <div className="card-surface p-5 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2"><h3 className="font-display text-2xl" data-testid="challenge-detail-title">{c.title}</h3><StatusPill s={c.status} /></div>
            <div className="text-xs text-zinc-400 mt-1">{TYPE_LABEL[c.type]}</div>
            <div className="text-xs text-zinc-500 mt-1">{fmt(c.start_at)} → {fmt(c.end_at)}{c.ended_at ? ` · ended ${fmt(c.ended_at)}` : ""}</div>
            {c.description && <p className="text-sm text-zinc-300 mt-2 whitespace-pre-wrap">{c.description}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            {c.status === "draft" && <Button data-testid="challenge-start-btn" size="sm" onClick={() => act("start")} className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-xs"><Play size={12} className="mr-1" /> Start</Button>}
            {c.status === "active" && <Button data-testid="challenge-pause-btn" size="sm" onClick={() => act("pause")} className="rounded-none bg-amber-600 hover:bg-amber-700 text-xs"><Pause size={12} className="mr-1" /> Pause</Button>}
            {paused && <Button data-testid="challenge-resume-btn" size="sm" onClick={() => act("resume")} className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-xs"><Play size={12} className="mr-1" /> Resume</Button>}
            {(c.status === "active" || paused) && <Button data-testid="challenge-end-btn" size="sm" onClick={() => act("end")} className="rounded-none bg-rose-700 hover:bg-rose-800 text-xs"><Square size={12} className="mr-1" /> End</Button>}
            {(c.status !== "ended" || me?.is_owner) && (
              <Button data-testid="challenge-edit-btn" size="sm" variant="ghost" onClick={() => onEdit(c)} className={`rounded-none border text-xs ${c.status === "ended" ? "border-amber-400/40 text-amber-300" : "border-white/15"}`}>
                <Pencil size={12} className="mr-1" /> {c.status === "ended" ? "Edit · Owner override" : "Edit"}
              </Button>
            )}
            <Button data-testid="challenge-audit-btn" size="sm" variant="ghost" onClick={toggleAudit} className="rounded-none border border-white/15 text-xs"><ScrollText size={12} className="mr-1" /> Audit</Button>
          </div>
        </div>
        <div className="grid grid-cols-3 lg:grid-cols-5 gap-2">
          {[["participants", "Participants", c.stats?.participants ?? 0, ""], ["qualified", "Qualified", c.stats?.qualified ?? 0, ""], ["disqualified", "Disqualified", c.stats?.disqualified ?? 0, ""], ["threshold", "Threshold", (c.threshold || 0).toLocaleString(), unit], ["total", "Total", (c.stats?.total_metric || 0).toLocaleString(), unit]].map(([k, l, v, u]) => (
            <div key={k} className="bg-black/30 border border-white/5 px-3 py-2 min-w-0">
              <div className="text-[9px] uppercase tracking-widest text-zinc-500 truncate">{l}</div>
              <div className="font-display text-lg leading-tight truncate" data-testid={`challenge-stat-${k}`}>{v}{u ? <span className="text-xs text-zinc-500 ml-1">{u}</span> : null}</div>
            </div>
          ))}
        </div>
        {c.prizes?.length > 0 && (
          <div className="text-xs text-zinc-400"><span className="uppercase tracking-widest text-zinc-500 text-[10px]">Prizes:</span>{" "}
            {c.prizes.map((p, i) => <span key={i} className="inline-block mr-3">#{p.rank_from}{p.rank_to !== p.rank_from ? `–${p.rank_to}` : ""}: {p.label}</span>)}
          </div>
        )}
        {paused && <div className="text-xs text-amber-300 border-l-2 border-amber-400 pl-2">Paused — activity during the pause does not count.</div>}
        {c.status === "ended" && c.final_results && <div className="text-xs text-zinc-500">Final results frozen at {fmt(c.final_results.frozen_at)}.</div>}
      </div>

      {showAudit && (
        <div className="card-surface p-4 space-y-1" data-testid="challenge-audit-list">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Audit trail (append-only)</div>
          {(audit || []).map((r) => (
            <div key={r.id} className="text-xs flex flex-wrap gap-2 bg-black/20 px-3 py-1.5 border-l-2 border-white/10">
              <span className="text-zinc-500 font-mono">{fmt(r.ts)}</span><span className="text-amber-300">{r.action}</span>
              <span className="text-zinc-400">@{r.actor_handle} ({r.actor_role})</span>{r.reason && <span className="text-zinc-500">— {r.reason}</span>}
              {r.result !== "success" && <span className="text-rose-400">{r.result}</span>}
            </div>
          ))}
          {audit && audit.length === 0 && <div className="text-xs text-zinc-600">No audit rows yet.</div>}
        </div>
      )}

      <div className="overflow-x-auto card-surface">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
            <th className="p-3">#</th><th className="p-3">Member</th><th className="p-3 text-right">{unit === "coins" ? "Coins spent" : "Bamboo earned"}</th><th className="p-3">Progress</th><th className="p-3" /></tr></thead>
          <tbody>
            {board.map((r) => (
              <tr key={r.user_id} className="border-b border-white/5" data-testid={`challenge-rank-row-${r.rank}`}>
                <td className="p-3 font-display text-lg">{r.rank}</td>
                <td className="p-3 flex items-center gap-2">
                  {r.avatar_url ? <img src={r.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" /> : <span className="w-6 h-6 rounded-full bg-zinc-800" />}
                  <span>@{r.handle || "?"}</span>
                </td>
                <td className="p-3 text-right font-mono text-amber-300">{r.total.toLocaleString()}</td>
                <td className="p-3">
                  <div className="h-1.5 w-28 bg-white/10"><div className={`h-full ${r.qualified ? "bg-emerald-500" : "bg-rose-500"}`} style={{ width: `${c.threshold ? Math.min(100, (r.total / c.threshold) * 100) : 100}%` }} /></div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{r.qualified ? "Qualified" : "Below threshold"}</div>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {c.status === "ended" && c.badge_award_enabled && !(c.awards || []).some((a) => a.user_id === r.user_id) && (
                    <Button data-testid={`challenge-award-badge-${r.user_id}`} onClick={() => awardBadge(r)} variant="ghost" className="h-7 rounded-none border border-amber-500/40 text-amber-300 text-xs px-2 mr-1 hover:bg-amber-950/40"><Award size={12} className="mr-1" /> Top Badge · 30d</Button>
                  )}
                  {(c.awards || []).some((a) => a.user_id === r.user_id) && <span className="text-[10px] text-amber-300 mr-2" data-testid={`challenge-awarded-${r.user_id}`}>Badge awarded</span>}
                  <Button data-testid={`challenge-dq-${r.user_id}`} onClick={() => disqualify(r)} variant="ghost" className="h-7 rounded-none border border-rose-900/50 text-rose-300 text-xs px-2 hover:bg-rose-950/40"><UserX size={12} className="mr-1" /> Disqualify</Button>
                </td>
              </tr>
            ))}
            {board.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-zinc-600 text-xs">No ranked activity yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {(c.disqualified_rows?.length > 0 || c.disqualified?.length > 0) && (
        <div className="card-surface p-4" data-testid="challenge-disqualified-list">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Disqualified</div>
          {(c.disqualified || []).map((d) => (
            <div key={d.user_id} className="flex flex-wrap items-center gap-3 text-xs py-1.5 border-b border-white/5">
              <span>@{d.handle}</span><span className="text-zinc-500">{d.reason}</span><span className="text-zinc-600">by @{d.by} · {fmt(d.at)}</span>
              <ConfirmButton label="Reinstate member" description={`Put @${d.handle} back on the leaderboard?`} onConfirm={() => reinstate(d)} testid={`challenge-reinstate-${d.user_id}`}>
                <RotateCcw size={12} className="mr-1" /> Reinstate
              </ConfirmButton>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
